"use server";

import { createClient } from "@/utils/supabase/server";
import { logAuthSecurityEvent } from "@/lib/security-events";
import {
  createPhoneVerification,
  checkPhoneVerification,
} from "@/lib/phone-verification/prelude";
import { saveVerifiedPhone } from "@/lib/phone-verification/profile-phone";

export type AuthActionResponse = {
  error?: string;
  success?: boolean | string;
  data?: { verificationId: string };
};

export async function sendPhoneOtpAction(formData: FormData): Promise<AuthActionResponse> {
  const phoneNumber = formData.get("phoneNumber")?.toString() || "";
  if (!phoneNumber) {
    return { error: "Phone number is required." };
  }

  try {
    const verificationId = await createPhoneVerification(phoneNumber);
    return {
      success: true,
      data: { verificationId },
    };
  } catch (error: unknown) {
    console.error("Error creating phone verification:", error);
    return {
      error: error instanceof Error ? error.message : "Failed to create phone verification.",
    };
  }
}

export async function verifyPhoneOtpAction(formData: FormData): Promise<AuthActionResponse> {
  const verificationId = formData.get("verificationId")?.toString() || "";
  const otp = formData.get("otp")?.toString() || "";
  const phoneNumber = formData.get("phoneNumber")?.toString() || "";
  let userId = formData.get("userId")?.toString();

  if (!verificationId || !otp) {
    return { error: "Missing verificationId or code." };
  }
  if (!phoneNumber) {
    return { error: "Missing phoneNumber." };
  }

  try {
    const check = await checkPhoneVerification({ verificationId, otp, phoneNumber });
    if (!check.ok) {
      await logAuthSecurityEvent({
        event_type: "auth.phone_verification_failure",
        user_id: null,
        success: false,
        failure_reason: check.error,
        formData,
      });
      return { error: check.error };
    }

    const supabase = await createClient();
    if (!userId) {
      const { data: userData, error: getUserError } = await supabase.auth.getUser();
      if (getUserError || !userData?.user) {
        return { error: "Could not retrieve user from session." };
      }
      userId = userData.user.id;
    }

    const saved = await saveVerifiedPhone(supabase, userId, phoneNumber);
    if (!saved.ok) {
      await logAuthSecurityEvent({
        event_type: "auth.phone_verification_failure",
        user_id: userId,
        success: false,
        failure_reason: saved.error,
        formData,
      });
      return { error: saved.error };
    }

    await logAuthSecurityEvent({
      event_type: "auth.phone_verification_success",
      user_id: userId,
      success: true,
      formData,
    });
    return { success: true };
  } catch (error: unknown) {
    await logAuthSecurityEvent({
      event_type: "auth.phone_verification_failure",
      user_id: null,
      success: false,
      failure_reason: error instanceof Error ? error.message : "Failed to verify phone OTP.",
      formData,
    });
    return {
      error: error instanceof Error ? error.message : "Failed to verify phone OTP.",
    };
  }
}
